<template lang="pug">
  .mt-marker
    slot(v-if='ready')
</template>

<script>
import * as TWEEN from '@tweenjs/tween.js'
import requestSmoothAnimation from '@/utils/requestSmoothAnimation'
import { createMarker, createLabel } from '@/utils/mapService'
import { MAP_MOVE_INTERVAL } from '@/app.config'

const animate = () => {
  if (TWEEN.update()) {
    requestSmoothAnimation(animate)
  }
}

export default {
  props: {
    heading: {
      type: Number,
      default: 0
    },
    isSeen: {
      type: Boolean,
      default: true
    },
    coordinates: {
      type: [Object, Array],
      required: true
    },
    options: {
      type: Object,
      default: () => ({
        symbol: {
          markerFile: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAXCAYAAAALHW+jAAACLUlEQVR42pVVOU8CQRQeVhMPVg2EIxyyAZZb/QMmVjbGo9JW7Szp9BdoY+ysrC01UTu10dhY06mxMBaCxsJE4xGv721Y8hhmVykeDDPf++Z7xzxEMpmchU37fL4BIYSHmXBYy6YFAoG+dDo9kclklgUtisViNZ/Pn8Tj8RECSCRCsdcgMwyjAN/jQqFQzeVyByIUCoXxowLSb3w/4JZ1EJvcSaUsEokY2Wx2FT535Au7JHHW7SAp02apVPoB4BM33qZSqbLf7++XFQWDQR34JeBuCEs+ZKZpruG8xyLUdT0Ikmv7kAwXfMDhPJFITAGjw3pxyTjCOsPZO8eSylgsFq9HY31oJJ+DGPgZJPs438H6yY6EG862wNFh59kKBbcPQ2VNRepmuOAJUYzyjrAT3wUl2+0SQsQRitjDu8Kuokb92C4hKrsodUJTFTuRj0ob4T7Cxys/CM6uUS8B/PIPwg9gFxQ9alVHsy0ajQ6imhd/EVIfgtDkvlyhJoW9+RchMLvAdrsp5JteKHh1yd0nXkxGMUy0pipzYijYgOOXguwb7bXn8tZbNq2ORwuNwflFQfiGRzApTSFO3qSskdhwOOyFylNFMSqYNAFF7gXPoXJwYoLMUL6kcFdsJQ5+6nlXt348rSum7p7moMsk11oam4GsffTaPBWH1CEF64qqtqWQimNAGc3KGsiHHPLmcXrLHkV+OqjREfphfYILh5YRTgrlvrT+iFCMORdsA/8L0ZEVOvtfbf0AAAAASUVORK5CYII='
          // markerWidth: 40,
          // markerHeight: 20
          // markerType: 'path',
          // markerPath: {
          //   // path: 'M 66.824406,237.27976 105,120.86309 143.17559,237.27976 105,223.975 Z',
          //   path: 'M 66.824406,120.65148 105,237.06815 143.17559,120.65148 105,133.95624 Z',
          //   stroke: '#000'
          // },
          // markerVerticalAlignment: 'top',
          // markerWidth: 400,
          // markerHeight: 200,
          // markerDx: 180,
          // markerDy: 180
        }
      })
    },
    textFill: {
      type: String,
      default: '#000'
    },
    textName: {
      type: String,
      default: ''
    }
  },
  methods: {
    move ({ isRotating, oldPosition: [ oldX, oldY ], newPosition: [ newX, newY ], newRotation, oldRotation }) {
      const from = {
        x: oldX,
        y: oldY,
        rotation: oldRotation
      }
      const to = {
        x: newX,
        y: newY,
        rotation: newRotation
      }
      new TWEEN.Tween(from)
        .to(to, MAP_MOVE_INTERVAL)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate(({ x, y, rotation }) => {
          this.mapObject.setCoordinates([ x, y ])
          this.mapObjectLabel.setCoordinates([ x, y ])
          if (isRotating) {
            this.mapObject.updateSymbol({ markerRotation: rotation })
          }
        })
        .start()

      animate()
    }
  },
  watch: {
    isSeen (newValue) {
      if (!this.mapObject) return

      if (newValue) {
        this.mapObject.show()
        this.mapObjectLabel.show()
      } else {
        this.mapObject.hide()
        this.mapObjectLabel.hide()
      }
    },
    coordinates (newValue, oldValue) {
      if (!this.mapObject) return
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      let newRotation = 180 - this.heading
      const oldRotation = (this.mapObject.getSymbol().markerRotation)

      const isRotating = (Math.abs(newRotation, oldRotation) < 90)
      if (!isRotating) {
        this.mapObject.updateSymbol({ markerRotation: newRotation })
      }
      this.move({
        newPosition: newValue,
        oldPosition: oldValue,
        newRotation,
        oldRotation,
        isRotating
      })
    }
  },
  data () {
    return {
      ready: false,
      mapObject: null
    }
  },
  mounted () {
    const vectorLayer = this.$parent.mapObject

    const options = {
      ...this.options
    }

    options.symbol = {
      ...options.symbol,
      markerFill: this.textFill,
      markerRotation: (180 - this.heading)
    }

    const marker = createMarker({
      coordinates: this.coordinates,
      options
    })

    const label = createLabel({
      label: this.textName,
      coordinates: this.coordinates,
      options: {
        boxStyle: {
          padding: [5, 5],
          symbol: {
            markerType: 'square',
            markerFill: this.textFill,
            markerFillOpacity: 0.8,
            markerLineWidth: 0
          }
        },
        textSymbol: {
          textDy: -30,
          textFill: '#fff',
          textWeight: 'bold'
        }
      }
    })

    label.addTo(vectorLayer)
    marker.addTo(vectorLayer)

    this.mapObject = marker
    this.mapObjectLabel = label
    this.ready = true
  },
  beforeDestroy () {
    this.mapObject.remove()
    this.mapObjectLabel.remove()
  }
}
</script>

<style scoped>
.mt-marker {
  display: none;
}
</style>
